<template>
  <div class="merchant-card">
    <div class="merchant-header">
      <div class="merchant-identity">
        <div class="merchant-top-row">
          <span class="merchant-cnpj">{{ formatCnpj(cnpj) }}</span>
          <span class="stores-count">
            {{ stores.length }} {{ stores.length === 1 ? "loja" : "lojas" }}
          </span>
        </div>
        <span v-if="companyName" class="merchant-company-name">{{
          companyName
        }}</span>
      </div>
    </div>

    <div class="table-wrapper">
      <table class="table is-fullwidth stores-table">
        <colgroup>
          <col style="width: 22%" />
          <col style="width: 8%" />
          <col style="width: 14%" />
          <col style="width: 12%" />
          <col style="width: 16%" />
          <col style="width: 20%" />
          <col style="width: 8%" />
        </colgroup>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Tipo</th>
            <th>UF - Cidade</th>
            <th>Bairro</th>
            <th>Logradouro</th>
            <th>ID Loja</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="store in stores"
            :key="store._id"
            :class="[
              'store-row',
              { 'store-row--clickable': permissions.manageBuyNowPayLater }
            ]"
            @click="goToStore(store)"
          >
            <td class="store-name cell-truncate" :title="store.storeInfo.name">
              {{ store.storeInfo.name }}
            </td>
            <td>
              <span
                :class="['tag', 'type-tag', typeClass(store.storeInfo.type)]"
              >
                {{ typeLabel(store.storeInfo.type) }}
              </span>
            </td>
            <td
              class="text-muted cell-truncate"
              :title="
                store.address && store.address.state
                  ? store.address.state + ' - ' + store.address.city
                  : '—'
              "
            >
              {{
                store.address && store.address.state
                  ? store.address.state + " - " + store.address.city
                  : "—"
              }}
            </td>
            <td
              class="text-muted cell-truncate"
              :title="(store.address && store.address.neighborhood) || '—'"
            >
              {{ (store.address && store.address.neighborhood) || "—" }}
            </td>
            <td
              class="text-muted cell-truncate"
              :title="formatAddress(store.address)"
            >
              {{ formatAddress(store.address) }}
            </td>
            <td class="store-id cell-truncate" :title="store._id">
              {{ store._id }}
            </td>
            <td>
              <span :class="['tag', statusClass(store.storeInfo.status)]">
                {{ statusLabel(store.storeInfo.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { common } from "../../mixins/common";
import { mapGetters } from "vuex";

export default {
  mixins: [common],

  props: {
    cnpj: { type: String, default: "" },
    companyName: { type: String, default: "" },
    stores: { type: Array, required: true }
  },

  methods: {
    formatCnpj(cnpj) {
      if (!cnpj) return "CNPJ não informado";
      const c = cnpj.replace(/\D/g, "");
      if (c.length !== 14) return cnpj;
      return c.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    formatAddress(address) {
      if (!address) return "—";
      const parts = [address.street, address.number].filter(Boolean);
      return parts.length ? parts.join(", ") : "—";
    },

    typeLabel(type) {
      const map = {
        market: "Market",
        box: "Box",
        station: "Station",
        vending: "Vending",
        locker: "Locker",
        refrigerator: "Refrigerador",
        refrigeratorTake: "Refrig. Take",
        rfid: "RFID",
        "distribution center": "CD",
        dc: "CD"
      };
      return map[type] || type || "—";
    },

    typeClass(type) {
      const map = {
        market: "type-market",
        box: "type-box",
        station: "type-station",
        vending: "type-vending",
        locker: "type-locker",
        refrigerator: "type-refrigerator",
        refrigeratorTake: "type-refrigerator",
        rfid: "type-rfid",
        "distribution center": "type-cd",
        dc: "type-cd"
      };
      return map[type] || "is-light";
    },

    statusClass(status) {
      const map = {
        active: "is-success",
        inactive: "is-danger",
        comingSoon: "is-warning",
        onMaintenance: "is-warning",
        showroom: "is-info"
      };
      return map[status] || "is-light";
    },

    statusLabel(status) {
      const map = {
        active: "Ativa",
        inactive: "Inativa",
        comingSoon: "Em breve",
        onMaintenance: "Manutenção",
        showroom: "Showroom"
      };
      return map[status] || status;
    },

    goToStore(store) {
      if (!this.permissions.manageBuyNowPayLater) return;
      this.$store
        .dispatch("setStoreBeingManaged", store)
        .then(() => this.$router.push("/configs"))
        .catch(() => this.alertError({ message: "UNEXPECTED_ERROR" }));
    }
  },

  computed: {
    ...mapGetters(["permissions"])
  }
};
</script>

<style scoped>
.merchant-card {
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 2px 4px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
  margin-bottom: 1.25rem;
  overflow: hidden;
  transition: box-shadow 0.25s ease;
}
.merchant-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08),
    0 4px 6px -4px rgba(0, 0, 0, 0.05);
}

.merchant-header {
  padding: 0.9rem 1.25rem 0.8rem;
  border-bottom: 1px solid #f3f4f6;
  background-color: #fafafa;
  border-radius: 0.75rem 0.75rem 0 0;
}

.merchant-top-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.2rem;
}

.merchant-cnpj {
  font-weight: 700;
  font-size: 1rem;
  color: #4a4453;
}

.stores-count {
  font-size: 0.72rem;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 9999px;
  padding: 0.15rem 0.55rem;
  font-weight: 500;
}

.merchant-company-name {
  font-size: 0.85rem;
  color: #6b7280;
}

.table-wrapper {
  overflow-x: auto;
}

.stores-table {
  margin-bottom: 0;
  font-size: 0.875rem;
  table-layout: fixed;
  width: 100%;
}

.cell-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 0;
}

.stores-table thead th {
  background-color: #f8f7fa;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #f3f4f6;
  padding: 0.6rem 0.85rem;
  white-space: nowrap;
}

.stores-table tbody td {
  vertical-align: middle;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid #f9fafb;
  color: #4a4453;
}

.stores-table tbody tr:last-child td {
  border-bottom: none;
}

.store-row {
  transition: background-color 0.15s ease;
}

.store-row--clickable {
  cursor: pointer;
}

.store-row--clickable:hover {
  background-color: #fff7f5;
}

.store-name {
  font-weight: 500;
  color: #4a4453;
}

.text-muted {
  color: #6b7280;
}

.store-id {
  font-family: monospace;
  font-size: 0.72rem;
  color: #9ca3af;
}

/* Type badges */
.type-tag {
  font-weight: 600;
  border: none;
  border-radius: 9999px;
  font-size: 0.72rem;
}
.type-market {
  background-color: #ff5722;
  color: #fff;
}
.type-box {
  background-color: #374151;
  color: #fff;
}
.type-station {
  background-color: #3b82f6;
  color: #fff;
}
.type-vending {
  background-color: #6b7280;
  color: #fff;
}
.type-locker {
  background-color: #7c3aed;
  color: #fff;
}
.type-refrigerator {
  background-color: #0ea5e9;
  color: #fff;
}
.type-rfid {
  background-color: #10b981;
  color: #fff;
}
.type-cd {
  background-color: #f59e0b;
  color: #fff;
}
</style>
